import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MediaLibrary from "@/pages/ph360/MediaLibrary";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/** Chapters tab — drag-and-drop reorder, manual chapter creation, edit, delete. */
export default function TabChapters({ project, onChange }) {
  const initialChapters = project.structured?.chapters || [];
  const [chapters, setChapters] = useState(initialChapters);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [heroFor, setHeroFor] = useState(null); // chapter for hero modal
  const [busy, setBusy] = useState(false);

  // Sync local chapter state when the underlying project version changes.
  // Intentionally keyed on updated_at (not project.structured.chapters) to
  // avoid re-syncing local edits mid-drag when the parent re-renders.
  useEffect(() => { setChapters(project.structured?.chapters || []); }, [project.updated_at]);  // eslint-disable-line react-hooks/exhaustive-deps

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = chapters.findIndex(c => c.__obj_id === active.id);
    const newIdx = chapters.findIndex(c => c.__obj_id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(chapters, oldIdx, newIdx);
    setChapters(reordered);
    try {
      const { data } = await api.post("/ph360/chapters/reorder", {
        ordered_obj_ids: reordered.map(c => c.__obj_id),
      });
      onChange(data);
      toast.success("Chapters reordered");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeChapter = async (obj_id) => {
    if (!window.confirm("Delete this chapter? This cannot be undone.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/ph360/chapters/${obj_id}`);
      onChange(data);
      toast.success("Chapter deleted");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <h2>Chapters</h2>
      <p>Drag chapters to reorder. Click any chapter to edit its title and content. Add a hero image per chapter — upload one or let AI generate one from the chapter's opening paragraphs.</p>

      <div className="ef360-actions">
        <button className="button button-primary" onClick={()=>setCreating(true)} disabled={busy} data-testid="chapter-add">+ Add chapter</button>
        <button className="button" onClick={()=>setCreating("part")} disabled={busy} data-testid="chapter-add-part">+ Add PART divider</button>
      </div>

      {creating && (
        <ChapterForm
          isPart={creating === "part"}
          onSave={async (payload) => {
            setBusy(true);
            try {
              const { data } = await api.post("/ph360/chapters", payload);
              onChange(data);
              setCreating(false);
              toast.success("Chapter added");
            } catch (err) { toast.error(formatApiError(err)); }
            finally { setBusy(false); }
          }}
          onCancel={()=>setCreating(false)}
        />
      )}

      {chapters.length === 0 ? (
        <div className="notice notice-info"><p>No chapters yet. Import a manuscript (Import tab) or add chapters manually above.</p></div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={chapters.map(c => c.__obj_id).filter(Boolean)} strategy={verticalListSortingStrategy}>
            <ul className="ef360-chapter-list" data-testid="chapters-list">
              {chapters.map((c) => (
                <SortableRow
                  key={c.__obj_id}
                  chapter={c}
                  onEdit={()=>setEditing(c)}
                  onHero={()=>setHeroFor(c)}
                  onDelete={()=>removeChapter(c.__obj_id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {editing && (
        <ChapterEditModal
          chapter={editing}
          onClose={()=>setEditing(null)}
          onSaved={(data)=>{ onChange(data); setEditing(null); }}
        />
      )}
      {heroFor && (
        <HeroImageModal
          chapter={heroFor}
          onClose={()=>setHeroFor(null)}
          onSaved={(data)=>{ onChange(data); setHeroFor(null); }}
        />
      )}
    </>
  );
}

function SortableRow({ chapter, onEdit, onHero, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: chapter.__obj_id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const isPart = chapter.type === "part";
  const wordCount = (chapter.blocks || []).reduce((s, b) => {
    const t = b.content || "";
    return s + (t ? t.split(/\s+/).filter(Boolean).length : 0);
  }, 0);
  return (
    <li ref={setNodeRef} style={style} className="ef360-chapter-row" data-testid={`chapter-row-${chapter.__obj_id}`}>
      <span className="ef360-chapter-handle" {...attributes} {...listeners} title="Drag to reorder" data-testid="chapter-handle">⋮⋮</span>
      <span className="ef360-chapter-num">{isPart ? "§" : String(chapter.number || 0).padStart(2, "0")}</span>
      <div className="ef360-chapter-hero-thumb">
        {chapter.hero_image ? (
          <img src={chapter.hero_image} alt="" data-testid={`chapter-hero-thumb-${chapter.__obj_id}`}/>
        ) : (
          <span className="ef360-hero-empty">—</span>
        )}
      </div>
      <div className="ef360-chapter-main">
        <div className="ef360-chapter-title">
          {isPart && <em style={{marginRight:6,color:"#8c8f94"}}>{chapter.label || "PART"}</em>}
          {chapter.title || "(untitled)"}
        </div>
        {chapter.subtitle && <div className="ef360-chapter-sub">{chapter.subtitle}</div>}
      </div>
      <span className="ef360-chapter-meta">{wordCount.toLocaleString()} words · {(chapter.blocks || []).length} blocks</span>
      {!isPart && (
        <button className="button" onClick={onHero} data-testid={`chapter-hero-${chapter.__obj_id}`}>
          {chapter.hero_image ? "🖼️ Change" : "🖼️ Hero"}
        </button>
      )}
      <button className="button" onClick={onEdit} data-testid={`chapter-edit-${chapter.__obj_id}`}>Edit</button>
      <button className="button delete" onClick={onDelete} data-testid={`chapter-delete-${chapter.__obj_id}`}>Delete</button>
    </li>
  );
}

function ChapterForm({ isPart, onSave, onCancel }) {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [content, setContent] = useState("");
  return (
    <div className="ef360-inline-form" data-testid="chapter-form">
      <h3>{isPart ? "New PART Divider" : "New Chapter"}</h3>
      <label>{isPart ? "Roman/Number label (e.g. I, II, One)" : "Chapter title"}</label>
      <input type="text" value={title} onChange={(e)=>setTitle(e.target.value)} data-testid="chapter-form-title" autoFocus/>
      {!isPart && (
        <>
          <label>Subtitle (optional)</label>
          <input type="text" value={subtitle} onChange={(e)=>setSubtitle(e.target.value)} data-testid="chapter-form-subtitle"/>
          <label>Content (paste or type — separate paragraphs with a blank line)</label>
          <textarea rows={10} value={content} onChange={(e)=>setContent(e.target.value)} data-testid="chapter-form-content"/>
        </>
      )}
      <p style={{marginTop:12}}>
        <button className="button button-primary" onClick={()=>onSave({ title, subtitle, content, chapter_type: isPart ? "part" : "chapter" })} data-testid="chapter-form-save">
          Add {isPart ? "PART" : "Chapter"}
        </button>{" "}
        <button className="button" onClick={onCancel} data-testid="chapter-form-cancel">Cancel</button>
      </p>
    </div>
  );
}

function ChapterEditModal({ chapter, onClose, onSaved }) {
  const initialHtml = (chapter.blocks || []).map(b => {
    const t = b.type;
    const c = (b.content || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    if (t === "h2") return `<h2>${c}</h2>`;
    if (t === "h3") return `<h3>${c}</h3>`;
    if (t === "list") return `<ul>${(b.items||[]).map(i=>`<li>${i}</li>`).join("")}</ul>`;
    if (t === "ordered_list") return `<ol>${(b.items||[]).map(i=>`<li>${i}</li>`).join("")}</ol>`;
    if (t === "pullquote") return `<blockquote>${c}</blockquote>`;
    if (t === "image" && b.url) {
      const alt = (b.alt || "").replace(/"/g,"&quot;");
      return `<p><img src="${b.url}" alt="${alt}" style="max-width:100%"/></p>`;
    }
    return `<p>${c}</p>`;
  }).join("");
  const [title, setTitle] = useState(chapter.title || "");
  const [subtitle, setSubtitle] = useState(chapter.subtitle || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const editorRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const exec = (cmd, arg=null) => { document.execCommand(cmd, false, arg); editorRef.current?.focus(); };

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/,"");
  const insertImageAtCursor = (url, alt="") => {
    editorRef.current?.focus();
    const safeAlt = (alt || "").replace(/"/g,"&quot;");
    const html = `<p><img src="${url}" alt="${safeAlt}" style="max-width:100%"/></p>`;
    document.execCommand("insertHTML", false, html);
  };

  const insertImageByUrl = () => {
    const url = window.prompt("Paste an image URL (https://…):");
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) { toast.error("Enter a full URL starting with http(s)://"); return; }
    insertImageAtCursor(url.trim(), "");
  };
  const insertHeroImage = () => {
    const hero = chapter.hero_image;
    if (!hero) { toast.info("This chapter has no hero image yet. Add one from the chapters list first."); return; }
    insertImageAtCursor(hero, chapter.title || "");
    toast.success("Chapter hero inserted");
  };
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/ph360/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const abs = data.url.startsWith("http") ? data.url : `${backendBase}${data.url}`;
      insertImageAtCursor(abs, file.name || "");
      toast.success("Image inserted");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const save = async () => {
    setBusy(true);
    try {
      // Sanitize on the way out so any pasted script/onerror payload is stripped
      // even if the browser's execCommand allowed it into the editable div.
      const rawHtml = editorRef.current?.innerHTML || initialHtml;
      const html = DOMPurify.sanitize(rawHtml, {
        ALLOWED_TAGS: ["p","br","strong","b","em","i","u","s","sub","sup","h1","h2","h3","h4","h5","h6",
          "ul","ol","li","blockquote","figure","figcaption","img","a","section","span","div","hr"],
        ALLOWED_ATTR: ["href","src","alt","title","class","id","style","target","rel"],
      });
      const { data } = await api.patch(`/ph360/chapters/${chapter.__obj_id}`, { title, subtitle, html_content: html });
      onSaved(data);
      toast.success("Chapter updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="ef360-modal" data-testid="chapter-edit-modal" onClick={onClose}>
      <div className="ef360-modal-body" onClick={(e)=>e.stopPropagation()} style={{maxWidth:820}}>
        <h3>Edit Chapter</h3>
        <label>Title</label>
        <input type="text" value={title} onChange={(e)=>setTitle(e.target.value)} data-testid="chapter-edit-title"/>
        <label>Subtitle</label>
        <input type="text" value={subtitle} onChange={(e)=>setSubtitle(e.target.value)} data-testid="chapter-edit-subtitle"/>
        <label>Content (rich text)</label>
        <div className="ef360-editor-toolbar" data-testid="chapter-edit-toolbar">
          {[["B","bold"],["I","italic"],["U","underline"],
            ["H2","formatBlock","h2"],["H3","formatBlock","h3"],["P","formatBlock","p"],
            ["• List","insertUnorderedList"],["1. List","insertOrderedList"],
            ["❝","formatBlock","blockquote"],
            ["← Undo","undo"],["→ Redo","redo"]].map(([lbl,cmd,arg])=>(
              <button key={lbl} type="button" onClick={()=>exec(cmd,arg)} data-testid={`chapter-edit-btn-${lbl}`}>{lbl}</button>
            ))}
          <button type="button" onClick={insertImageByUrl} data-testid="chapter-edit-btn-img-url">🖼 URL</button>
          <button type="button" onClick={()=>fileInputRef.current?.click()} disabled={uploading} data-testid="chapter-edit-btn-img-upload">
            {uploading ? "Uploading…" : "⬆ Upload"}
          </button>
          <button type="button" onClick={insertHeroImage} data-testid="chapter-edit-btn-hero">✨ Hero</button>
          <button type="button" onClick={()=>setShowLibrary(true)} data-testid="chapter-edit-btn-library">📚 Library</button>
          <input ref={fileInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={onUpload} data-testid="chapter-edit-file-input"/>
        </div>
        {showLibrary && (
          <MediaLibrary
            onClose={()=>setShowLibrary(false)}
            onInsert={(url, name)=>insertImageAtCursor(url, name)}
          />
        )}
        <div
          ref={editorRef}
          className="ef360-final-editor"
          contentEditable
          suppressContentEditableWarning
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(initialHtml, {
            ALLOWED_TAGS: ["p","br","strong","b","em","i","u","s","sub","sup","h1","h2","h3","h4","h5","h6",
              "ul","ol","li","blockquote","figure","figcaption","img","a","section","span","div","hr"],
            ALLOWED_ATTR: ["href","src","alt","title","class","id","style","target","rel"],
          }) }}
          data-testid="chapter-edit-content"
          style={{minHeight:280}}
        />
        <p style={{marginTop:12}}>
          <button className="button button-primary" onClick={save} disabled={busy} data-testid="chapter-edit-save">
            {busy ? "Saving…" : "Save changes"}
          </button>{" "}
          <button className="button" onClick={onClose} data-testid="chapter-edit-cancel">Cancel</button>
        </p>
      </div>
    </div>
  );
}

function HeroImageModal({ chapter, onClose, onSaved }) {
  const [mode, setMode] = useState(chapter.hero_image_source === "ai" ? "ai" : "upload");
  const [prompt, setPrompt] = useState(chapter.hero_image_prompt || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const uploadImage = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose an image file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/ph360/chapters/${chapter.__obj_id}/hero-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Hero image uploaded");
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const generateAI = async () => {
    setBusy(true);
    toast.info("Generating hero image with Nano Banana… this can take 10–30 seconds.");
    try {
      const { data } = await api.post(`/ph360/chapters/${chapter.__obj_id}/hero-image/generate`, {
        prompt: prompt || null,
      });
      toast.success("Hero image generated");
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const removeImage = async () => {
    if (!window.confirm("Remove this chapter's hero image?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/ph360/chapters/${chapter.__obj_id}/hero-image`);
      toast.success("Hero image removed");
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="ef360-modal" data-testid="hero-image-modal" onClick={onClose}>
      <div className="ef360-modal-body" onClick={(e)=>e.stopPropagation()} style={{maxWidth:680}}>
        <h3>Chapter Hero Image · {chapter.title || `Chapter ${chapter.number}`}</h3>

        {chapter.hero_image && (
          <div style={{margin:"10px 0 16px", textAlign:"center"}}>
            <img src={chapter.hero_image} alt="" style={{maxWidth:"100%", maxHeight:200, borderRadius:6, boxShadow:"0 2px 8px rgba(0,0,0,0.12)"}} data-testid="hero-current-preview"/>
            <div style={{fontSize:12, color:"#646970", marginTop:6}}>
              Current source: <strong>{chapter.hero_image_source || "unknown"}</strong>
            </div>
          </div>
        )}

        <div className="ef360-hero-tabs">
          <button
            className={`ef360-hero-tab ${mode === "upload" ? "active" : ""}`}
            onClick={()=>setMode("upload")}
            data-testid="hero-mode-upload"
          >📁 Upload image</button>
          <button
            className={`ef360-hero-tab ${mode === "ai" ? "active" : ""}`}
            onClick={()=>setMode("ai")}
            data-testid="hero-mode-ai"
          >✨ AI generate (Nano Banana)</button>
        </div>

        {mode === "upload" ? (
          <form onSubmit={uploadImage}>
            <label>Choose image (JPEG/PNG/WebP, max 6MB)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e)=>setFile(e.target.files?.[0] || null)}
              data-testid="hero-upload-file"
            />
            <p style={{marginTop:12}}>
              <button type="submit" className="button button-primary" disabled={busy || !file} data-testid="hero-upload-submit">
                {busy ? "Uploading…" : "Upload hero image"}
              </button>
            </p>
          </form>
        ) : (
          <>
            <label>Custom prompt (optional — leave blank to auto-generate from chapter content)</label>
            <textarea
              rows={4}
              value={prompt}
              onChange={(e)=>setPrompt(e.target.value)}
              placeholder="e.g., a quiet library at dusk, warm side-light, soft golden atmosphere"
              data-testid="hero-ai-prompt"
            />
            <p className="description">
              We include an editorial-book style directive automatically (no text, no watermarks, landscape composition).
            </p>
            <p style={{marginTop:12}}>
              <button className="button button-primary" onClick={generateAI} disabled={busy} data-testid="hero-ai-submit">
                {busy ? "Generating…" : "✨ Generate hero image"}
              </button>
            </p>
          </>
        )}

        <p style={{marginTop:14, display:"flex", gap:10}}>
          {chapter.hero_image && (
            <button className="button delete" onClick={removeImage} disabled={busy} data-testid="hero-remove">
              Remove hero image
            </button>
          )}
          <button className="button" onClick={onClose} data-testid="hero-cancel">Close</button>
        </p>
      </div>
    </div>
  );
}
